/**
 * Pinned Question Model
 *
 * Handles API operations for individual pinned questions
 * Corresponds to the pinned_questions table in the backend
 * Follows the warbler pattern similar to SupplierModel
 */

import { get, post, put, del } from '@/services/http';
import {
  QuestionCategory,
  QuestionStatus,
  QuestionSource,
  QuestionPriority,
} from '@/models/insights/';

export class PinnedQuestionModel {

  /**
   * Get pinned question by ID
   */
  public static async getById(id: number): Promise<PinnedQuestionModel | null> {
    try {
      const response: any = await get(`/pinned-questions/${id}`);
      if (response.data && response.data.success && response.data.data) {
        return new PinnedQuestionModel(response.data.data);
      }
      return null;
    } catch (error) {
      console.error('Error fetching pinned question:', error);
      return null;
    }
  }

  /**
   * Get all pinned questions with optional filtering
   */
  public static async getAll(params?: any): Promise<PinnedQuestionModel[]> {
    try {
      const response: any = await get('/pinned-questions', { params });
      if (response.data && response.data.success && response.data.data) {
        const questions = response.data.data.map((item: any) => new PinnedQuestionModel(item));
        // Sort by display_order
        return questions.sort((a: PinnedQuestionModel, b: PinnedQuestionModel) => a.display_order - b.display_order);
      }
      return [];
    } catch (error) {
      console.error('Error fetching pinned questions:', error);
      return [];
    }
  }

  /**
   * Bulk reorder pinned questions
   */
  public static async bulkReorder(orderUpdates: Array<{id: number, display_order: number}>): Promise<boolean> {
    try {
      const response: any = await post('/pinned-questions/reorder', {
        order_updates: orderUpdates,
      });

      return response.data && response.data.success;
    } catch (error) {
      console.error('Error bulk reordering:', error);
      return false;
    }
  }
  public id: number;
  public question_text: string;
  public instruction: string;
  public question_hash: string;
  public parameters?: any;
  public measure_code: string;
  public dimension_code?: string;
  public user_id?: number;
  public merchant_id: number;
  public status: QuestionStatus;
  public source: QuestionSource;
  public execution_count: number;
  public created_at: string;
  public updated_at: string;
  public last_executed_at?: string;
  public category?: string;
  public priority: QuestionPriority;
  public display_order: number;
  public pin_reason?: string;
  public pin_context?: string;
  public pinned_at?: string;
  public metadata?: any;
  public analytics?: any;

  constructor(data: any = {}) {
    // Extract parameters from API response if present
    const parameters = (data as any).parameters;
    
    this.id = data.id || 0;
    this.question_text = data.question_text || '';
    this.instruction = data.instruction || '';
    this.question_hash = data.question_hash || '';
    this.parameters = (data as any).parameters || parameters;
    
    // Extract measure_code and dimension_code from parameters object if available
    // API response structure: { parameters: { measures: ["code"], dimensions: ["code"] } }
    this.measure_code = data.measure_code ||
                       (parameters?.measures?.[0]) ||
                       '';
    this.dimension_code = data.dimension_code ||
                         (parameters?.dimensions?.[0]) ||
                         undefined;
    this.user_id = data.user_id;
    this.merchant_id = data.merchant_id || 0;
    this.status = data.status || QuestionStatus.ACTIVE;
    this.source = data.source || QuestionSource.CUSTOM;
    this.execution_count = data.execution_count || 0;
    this.created_at = data.created_at || '';
    this.updated_at = data.updated_at || '';
    this.last_executed_at = data.last_executed_at;
    this.category = data.category;
    this.priority = data.priority || QuestionPriority.MEDIUM;
    this.display_order = data.display_order || 0;
    this.pin_reason = data.pin_reason || 'frequently_used';
    this.pin_context = data.pin_context;
    this.pinned_at = data.pinned_at || new Date().toISOString();
    this.metadata = data.metadata;
    this.analytics = data.analytics;
  }

  /**
   * Save pinned question (create or update)
   */
  public async save(): Promise<boolean> {
    try {
      if (this.id) {
        await this.update();
      } else {
        await this.create();
      }
      return true;
    } catch (error) {
      console.error('Error saving pinned question:', error);
      return false;
    }
  }

  /**
   * Delete pinned question (unpin)
   */
  public async delete(): Promise<boolean> {
    try {
      const response: any = await del(`/pinned-questions/${this.id}`);
      if (response.data && response.success) {
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting pinned question:', error);
      return false;
    }
  }

  /**
   * Update display order
   */
  public async updateOrder(newOrder: number): Promise<boolean> {
    try {
      const response: any = await put(`/pinned-questions/${this.id}/order`, {
        display_order: newOrder,
      });

      if (response.data && response.success) {
        this.display_order = newOrder;
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error updating order:', error);
      return false;
    }
  }

  /**
   * Execute the question to get fresh results
   */
  public async execute(): Promise<any> {
    try {
      const response: any = await post(`/pinned-questions/${this.id}/execute`);

      if (response.data && response.data.success && response.data.data) {
        // Update execution count and last executed time
        this.execution_count++;
        this.last_executed_at = new Date().toISOString();

        // Update analytics if returned
        if (response.data.data.analytics) {
          this.analytics = response.data.data.analytics;
        }

        return response.data.data;
      }

      return null;
    } catch (error) {
      console.error('Error executing question:', error);
      throw error;
    }
  }

  /**
   * Get category icon for UI display
   */
  public getCategoryIcon(): string {
    const category = this.getCategoryFromMetadata();
    
    // Return emoji icons for QuestionCard display
    const emojiIconMap: Record<string, string> = {
      [QuestionCategory.SALES_OVERVIEW]: '📈',
      [QuestionCategory.INVENTORY_FORECASTING]: '📦',
      [QuestionCategory.CUSTOMER_INSIGHTS]: '👥',
      [QuestionCategory.PRODUCT_ANALYTICS]: '🚀',
      [QuestionCategory.FINANCIAL_REPORTING]: '💰',
      [QuestionCategory.OPERATIONS_MONITORING]: '⚙️',
      [QuestionCategory.GENERAL_QUERY]: '❓',
      [QuestionCategory.ORDER_ANALYTICS]: '🛒',
      [QuestionCategory.MARKETING_ANALYTICS]: '📢',
      [QuestionCategory.SUPPLIER_ANALYTICS]: '🚚',
      [QuestionCategory.CUSTOM]: '⭐',
    };
    
    // Check for custom icon in metadata first
    if (this.metadata?.icon) {
      return this.metadata.icon;
    }
    
    // Check measure_code for specific mappings
    const measureCode = this.measure_code.toLowerCase();
    if (measureCode.includes('sales') || measureCode.includes('revenue')) {
      return '📈';
    } else if (measureCode.includes('inventory')) {
      if (measureCode.includes('replenishment')) {
        return '🔄';
      } else if (measureCode.includes('quantity') || measureCode.includes('stock')) {
        return '📊';
      }
      return '📦';
    } else if (measureCode.includes('customer')) {
      return '👥';
    } else if (measureCode.includes('product')) {
      return '🚀';
    } else if (measureCode.includes('financial') || measureCode.includes('profit')) {
      return '💰';
    } else if (measureCode.includes('order')) {
      return '🛒';
    } else if (measureCode.includes('supplier') || measureCode.includes('vendor')) {
      return '🚚';
    }
    
    return emojiIconMap[category] || '📊';
  }

  /**
   * Get category color for UI display
   */
  public getCategoryColor(): string {
    const category = this.getCategoryFromMetadata();
    const colorMap: Record<string, string> = {
      [QuestionCategory.SALES_OVERVIEW]: 'success',
      [QuestionCategory.INVENTORY_FORECASTING]: 'warning',
      [QuestionCategory.CUSTOMER_INSIGHTS]: 'info',
      [QuestionCategory.PRODUCT_ANALYTICS]: 'primary',
      [QuestionCategory.FINANCIAL_REPORTING]: 'secondary',
      [QuestionCategory.OPERATIONS_MONITORING]: 'accent',
      [QuestionCategory.GENERAL_QUERY]: 'grey',
      [QuestionCategory.ORDER_ANALYTICS]: 'indigo',
      [QuestionCategory.MARKETING_ANALYTICS]: 'purple',
      [QuestionCategory.SUPPLIER_ANALYTICS]: 'teal',
      [QuestionCategory.CUSTOM]: 'deep-purple',
    };
    return this.metadata?.color || colorMap[category] || 'grey';
  }

  /**
   * Get priority color for UI display
   */
  public getPriorityColor(): string {
    const colorMap: Record<QuestionPriority, string> = {
      [QuestionPriority.HIGH]: 'error',
      [QuestionPriority.MEDIUM]: 'warning',
      [QuestionPriority.LOW]: 'success',
    };
    return colorMap[this.priority] || 'grey';
  }

  /**
   * Get priority icon for UI display
   */
  public getPriorityIcon(): string {
    const iconMap: Record<QuestionPriority, string> = {
      [QuestionPriority.HIGH]: 'mdi-chevron-double-up',
      [QuestionPriority.MEDIUM]: 'mdi-chevron-up',
      [QuestionPriority.LOW]: 'mdi-chevron-down',
    };
    return iconMap[this.priority] || 'mdi-minus';
  }

  /**
   * Get pin reason display text
   */
  public getPinReasonText(): string {
    const reasonMap: Record<string, string> = {
      'frequently_used': 'Frequently Used',
      'user_preference': 'User Preference',
      'recommended': 'Recommended',
      'critical_metric': 'Critical Metric',
      'custom': 'Custom',
    };
    return reasonMap[this.pin_reason || ''] || 'Pinned';
  }

  /**
   * Get formatted pinned date
   */
  public getFormattedPinnedDate(): string {
    if (!this.pinned_at) {
      return 'Unknown';
    }

    const date = new Date(this.pinned_at);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));

    if (diffDays === 0) { return 'Today'; }
    if (diffDays === 1) { return 'Yesterday'; }
    if (diffDays < 7) { return `${diffDays} days ago`; }
    if (diffDays < 30) { return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) === 1 ? '' : 's'} ago`; }

    return date.toLocaleDateString();
  }

  /**
   * Get formatted last executed date
   */
  public getFormattedLastExecuted(): string {
    if (!this.last_executed_at) {
      return 'Never';
    }

    const date = new Date(this.last_executed_at);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) { return 'Just now'; }
    if (diffMins < 60) { return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`; }

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) { return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`; }

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) { return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`; }

    return date.toLocaleDateString();
  }

  /**
   * Check if question needs refresh based on metadata
   */
  public needsRefresh(): boolean {
    if (!this.metadata?.data_freshness) {
      return false;
    }

    if (!this.last_executed_at) {
      return true;
    }

    const lastExecuted = new Date(this.last_executed_at);
    const now = new Date();
    const diffMs = now.getTime() - lastExecuted.getTime();

    const freshnessThresholds: Record<string, number> = {
      real_time: 5 * 60 * 1000,        // 5 minutes
      hourly: 60 * 60 * 1000,          // 1 hour
      daily: 24 * 60 * 60 * 1000,      // 1 day
      weekly: 7 * 24 * 60 * 60 * 1000, // 1 week
      monthly: 30 * 24 * 60 * 60 * 1000, // 30 days
    };

    const threshold = freshnessThresholds[this.metadata.data_freshness] || freshnessThresholds.daily;
    return diffMs > threshold;
  }

  /**
   * Convert to JSON representation
   */
  public toJSON(): any {
    return {
      id: this.id,
      question_text: this.question_text,
      instruction: this.instruction,
      question_hash: this.question_hash,
      parameters: this.parameters,
      measure_code: this.measure_code,
      dimension_code: this.dimension_code,
      user_id: this.user_id,
      merchant_id: this.merchant_id,
      status: this.status,
      source: this.source,
      execution_count: this.execution_count,
      created_at: this.created_at,
      updated_at: this.updated_at,
      last_executed_at: this.last_executed_at,
      category: this.category,
      priority: this.priority,
      display_order: this.display_order,
      pin_reason: this.pin_reason,
      pin_context: this.pin_context,
      pinned_at: this.pinned_at,
      metadata: this.metadata,
      analytics: this.analytics,
    };
  }

  /**
   * Create new pinned question
   */
  private async create(): Promise<void> {
    const now = new Date().toISOString();
    
    // Debug logging
    console.log('Creating pinned question with data:', {
      question_text: this.question_text,
      question_hash: this.question_hash,
      measure_code: this.measure_code,
      category: this.category
    });
    
    const payload: any = {
      question_text: this.question_text,
      question_hash: this.question_hash,
      parameters: this.parameters || {},
      measure_code: this.measure_code,
      dimension_code: this.dimension_code,
      category: this.category || this.getCategoryFromMetadata(),
      priority: this.priority,
      pin_reason: this.pin_reason,
      execution_count: this.execution_count,
      last_executed_at: this.last_executed_at || now,
      metadata: {
        pin_context: this.pin_context,
        priority: this.priority,
        ...this.metadata
      },
    };

    const response: any = await post('/pinned-questions', payload);

    if (response.data && response.data.success && response.data.data) {
      this.updateFromResponse(response.data.data);
    }
  }

  /**
   * Update existing pinned question
   */
  private async update(): Promise<void> {
    const payload = {
      question_text: this.question_text,
      instruction: this.instruction,
      measure_code: this.measure_code,
      dimension_code: this.dimension_code,
      category: this.category,
      priority: this.priority,
      pin_reason: this.pin_reason,
      pin_context: this.pin_context,
      metadata: this.metadata,
    };

    const response: any = await put(`/pinned-questions/${this.id}`, payload);

    if (response.data && response.data.success && response.data.data) {
      this.updateFromResponse(response.data.data);
    }
  }

  /**
   * Get category from metadata or measure code
   */
  private getCategoryFromMetadata(): string {
    // Return existing category if set
    if (this.category) { return this.category; }

    // Check metadata tags
    if (this.metadata?.tags?.length) {
      const categoryTag = this.metadata.tags.find((tag: any) =>
        Object.values(QuestionCategory).includes(tag as QuestionCategory),
      );
      if (categoryTag) { return categoryTag; }
    }

    // Map measure code to category
    const measureCategoryMap: Record<string, string> = {
      sales: QuestionCategory.SALES_OVERVIEW,
      inventory: QuestionCategory.INVENTORY_FORECASTING,
      customer: QuestionCategory.CUSTOMER_INSIGHTS,
      product: QuestionCategory.PRODUCT_ANALYTICS,
      financial: QuestionCategory.FINANCIAL_REPORTING,
      operations: QuestionCategory.OPERATIONS_MONITORING,
      order: QuestionCategory.ORDER_ANALYTICS,
      marketing: QuestionCategory.MARKETING_ANALYTICS,
      supplier: QuestionCategory.SUPPLIER_ANALYTICS,
    };

    for (const [key, category] of Object.entries(measureCategoryMap)) {
      if (this.measure_code.toLowerCase().includes(key)) {
        return category;
      }
    }

    return QuestionCategory.GENERAL_QUERY;
  }

  /**
   * Update model from API response
   */
  private updateFromResponse(data: any): void {
    this.id = data.id;
    this.question_text = data.question_text;
    this.instruction = data.instruction;
    this.question_hash = data.question_hash;
    this.parameters = data.parameters;
    this.measure_code = data.measure_code;
    this.dimension_code = data.dimension_code;
    this.user_id = data.user_id;
    this.merchant_id = data.merchant_id;
    this.status = data.status;
    this.source = data.source;
    this.execution_count = data.execution_count;
    this.created_at = data.created_at;
    this.updated_at = data.updated_at;
    this.last_executed_at = data.last_executed_at;
    this.category = data.category;
    this.priority = data.priority;
    this.display_order = data.display_order;
    this.pin_reason = data.pin_reason;
    this.pin_context = data.pin_context;
    this.pinned_at = data.pinned_at;
    this.metadata = data.metadata;
    this.analytics = data.analytics;
  }
}
